<template>
  <div class="appraisal-detail-page">
    <b-overlay :show="loading" rounded="sm">
      <div v-if="appraisal">
        <!-- Header -->
        <div class="page-header">
          <div class="header-title">
            <h2 class="page-title">{{ appraisal.name }}</h2>
            <p class="page-subtitle">{{ appraisal.description }}</p>
          </div>

          <!-- Status Cards Row with Back Button on far right -->
          <div class="status-row">
            <div class="status-cards-wrapper">
              <div class="status-card">
                <div class="status-icon period">
                  <i class="fa fa-calendar"></i>
                </div>
                <div class="status-info">
                  <span class="status-label">Period</span>
                  <span class="status-value">{{ formatPeriod }}</span>
                </div>
              </div>
              <div class="status-card">
                <div class="status-icon employee">
                  <i class="fa fa-user"></i>
                </div>
                <div class="status-info">
                  <span class="status-label">Employee</span>
                  <span class="status-value">{{ appraisal.appraised_user?.name || '-' }}</span>
                </div>
              </div>
              <div class="status-card">
                <div class="status-icon manager">
                  <i class="fa fa-user-tie"></i>
                </div>
                <div class="status-info">
                  <span class="status-label">Manager</span>
                  <span class="status-value">{{ appraisal.manager?.name || '-' }}</span>
                </div>
              </div>
              <div v-if="(appraisal.enable_independent_reviewer || isReviewerOnlyMode) && appraisal.reviewer" class="status-card">
                <div class="status-icon reviewer">
                  <i class="fa fa-user-check"></i>
                </div>
                <div class="status-info">
                  <span class="status-label">Reviewer</span>
                  <span class="status-value">{{ appraisal.reviewer?.name || '-' }}</span>
                </div>
              </div>
              <div class="status-card">
                <div class="status-icon score" :class="scoreClass">
                  <i class="fa fa-chart-line"></i>
                </div>
                <div class="status-info">
                  <span class="status-label">Final Score</span>
                  <span class="status-value score-value" :class="scoreClass">
                    {{ appraisal.final_score !== null ? appraisal.final_score + '%' : 'Pending' }}
                  </span>
                </div>
              </div>
            </div>
            <div class="header-actions">
              <b-button variant="outline-primary" size="sm" class="btn-print mr-2" @click="printAppraisal">
                <i class="fa fa-print"></i> Print
              </b-button>
              <b-button variant="outline-secondary" size="sm" class="btn-back" @click="$router.back()">
                <i class="fa fa-arrow-left"></i> Back
              </b-button>
            </div>
          </div>
        </div>

        <!-- Progress Indicator - New Row -->
        <div class="progress-card">
          <div class="progress-steps">
              <div class="progress-step" :class="{ completed: true }">
                <div class="step-circle"><i class="fa fa-check"></i></div>
                <span class="step-label">Assigned</span>
              </div>
              <div class="progress-line" :class="{ active: appraisal.self_score_submitted }"></div>
              <div class="progress-step" :class="{ completed: appraisal.self_score_submitted, active: !appraisal.self_score_submitted && canSubmitSelfScore }">
                <div class="step-circle">
                  <i v-if="appraisal.self_score_submitted" class="fa fa-check"></i>
                  <span v-else>2</span>
                </div>
                <span class="step-label">Self Assessment</span>
              </div>
              <div class="progress-line" :class="{ active: appraisal.manager_score_submitted }"></div>
              <div class="progress-step" :class="{ completed: appraisal.manager_score_submitted, active: appraisal.self_score_submitted && !appraisal.manager_score_submitted && canSubmitManagerScore }">
                <div class="step-circle">
                  <i v-if="appraisal.manager_score_submitted" class="fa fa-check"></i>
                  <span v-else>3</span>
                </div>
                <span class="step-label">Manager Review</span>
              </div>
              <template v-if="(appraisal.enable_independent_reviewer || isReviewerOnlyMode) && appraisal.reviewer">
                <div class="progress-line" :class="{ active: appraisal.reviewer_score_submitted }"></div>
                <div class="progress-step" :class="{ completed: appraisal.reviewer_score_submitted, active: appraisal.manager_score_submitted && !appraisal.reviewer_score_submitted && canSubmitReviewerScore }">
                  <div class="step-circle">
                    <i v-if="appraisal.reviewer_score_submitted" class="fa fa-check"></i>
                    <span v-else>4</span>
                  </div>
                  <span class="step-label">{{ isReviewerOnlyMode ? 'Reviewer Scoring' : 'Independent Review' }}</span>
                </div>
              </template>
              <div class="progress-line" :class="{ active: appraisal.final_score !== null }"></div>
              <div class="progress-step" :class="{ completed: appraisal.final_score !== null }">
                <div class="step-circle">
                  <i v-if="appraisal.final_score !== null" class="fa fa-check"></i>
                  <span v-else>{{ (appraisal.enable_independent_reviewer || isReviewerOnlyMode) && appraisal.reviewer ? 5 : 4 }}</span>
                </div>
                <span class="step-label">Completed</span>
              </div>
          </div>
        </div>

        <!-- Dispute Banner -->
        <div v-if="appraisal.is_disputed" class="dispute-banner mb-3" :class="{ 'resolved': appraisal.dispute_resolved || appraisal.reviewer_score_submitted }">
          <div class="dispute-icon">
            <i :class="appraisal.dispute_resolved || appraisal.reviewer_score_submitted ? 'fa fa-check-circle' : 'fa fa-exclamation-triangle'"></i>
          </div>
          <div class="dispute-content">
            <template v-if="appraisal.dispute_resolved || appraisal.reviewer_score_submitted">
              <strong class="text-success">Dispute Resolved</strong>
              <p class="mb-0 text-muted">The independent reviewer has made the final ruling.</p>
            </template>
            <template v-else>
              <strong>This appraisal is under dispute</strong>
              <p v-if="appraisal.dispute_reason" class="mb-0">Reason: {{ appraisal.dispute_reason }}</p>
              <p class="mb-0 text-muted">The independent reviewer will make the final ruling.</p>
            </template>
          </div>
        </div>

        <!-- Criteria Section -->
        <div class="criteria-section">
          <div class="section-header">
            <h5 class="section-title">
              <i class="fa fa-clipboard-list"></i> Evaluation Criteria
            </h5>
            <div class="section-header-actions">
              <span class="criteria-count">{{ appraisal.criteria?.length || 0 }} items</span>
              <b-button
                v-if="canAddCriteria"
                variant="outline-primary"
                size="sm"
                @click="showAddCriteriaModal = true"
              >
                <i class="fa fa-plus"></i> Add Criteria
              </b-button>
            </div>
          </div>

          <div class="criteria-list">
            <div v-for="group in groupedCriteria" :key="group.name" class="criteria-group">
              <div class="category-header">
                <i class="fa fa-folder-open"></i>
                <span class="category-name">{{ group.name }}</span>
                <span class="category-count">{{ group.items.length }} {{ group.items.length === 1 ? 'item' : 'items' }}</span>
              </div>

              <div v-for="item in group.items" :key="item.id" class="criteria-item">
                <div class="criteria-header">
                  <div class="criteria-info">
                    <span class="criteria-number">{{ item.globalIndex }}</span>
                    <div class="criteria-text">
                      <h6 class="criteria-name">{{ item.name }}</h6>
                      <p v-if="item.description" class="criteria-description">{{ item.description }}</p>
                    </div>
                  </div>
                  <div class="criteria-badges">
                    <span class="weight-badge" :class="{ bonus: item.is_bonus }">
                      {{ item.is_bonus ? 'Bonus' : '' }} {{ item.weight }}%
                    </span>
                    <span
                      v-if="getAttainedWeight(item) !== null"
                      class="attained-badge"
                      :class="getAttainedWeightClass(item)"
                      :title="'Attained ' + getAttainedWeight(item).toFixed(1) + '% out of ' + item.weight + '%'"
                    >
                      <i class="fa fa-check-circle mr-1"></i>{{ getAttainedWeight(item).toFixed(1) }}%
                    </span>
                    <div v-if="canEditCriteria" class="criteria-actions ml-2">
                      <b-button
                        variant="outline-secondary"
                        size="sm"
                        class="btn-icon mr-1"
                        @click="openEditCriteriaModal(item)"
                        title="Edit Criteria"
                      >
                        <i class="fa fa-pencil-alt"></i>
                      </b-button>
                      <b-button
                        variant="outline-danger"
                        size="sm"
                        class="btn-icon"
                        @click="confirmDeleteCriteria(item)"
                        title="Delete Criteria"
                      >
                        <i class="fa fa-trash"></i>
                      </b-button>
                    </div>
                  </div>
                </div>

              <div class="scoring-grid">
                <!-- Self Assessment -->
                <div v-if="canSubmitSelfScore || appraisal.self_score_submitted" class="scoring-column self">
                  <div class="scoring-header">
                    <i class="fa fa-user"></i>
                    <span>Self Assessment</span>
                    <span v-if="appraisal.self_score_submitted" class="submitted-badge">
                      <i class="fa fa-check-circle"></i> Submitted
                    </span>
                  </div>
                  
                  <div class="scoring-body">
                    <template v-if="item.target_type && item.target_type !== 'score'">
                      <div class="form-group">
                        <label>Target: <strong>{{ formatTargetValue(item) }}</strong></label>
                        <b-form-input
                          v-model="scores[item.id].actual_value"
                          type="number"
                          :placeholder="'Enter actual ' + getTargetLabel(item.target_type)"
                          :disabled="appraisal.self_score_submitted || !canSubmitSelfScore"
                          class="form-control-modern"
                        ></b-form-input>
                      </div>
                      <div v-if="scores[item.id].actual_value" class="form-group">
                        <label>Achievement</label>
                        <div class="achievement-display">
                          <span class="achievement-value" :class="getAchievementClass(item)">
                            {{ calculateAchievement(item) }}%
                          </span>
                          <small class="text-muted ml-2">({{ scores[item.id].actual_value }} / {{ item.target_value }})</small>
                        </div>
                      </div>
                    </template>

                    <div v-if="!item.target_type || item.target_type === 'score'" class="form-group">
                      <label>Score</label>
                      <div class="score-selector">
                        <button
                          v-for="n in parseInt(appraisal.score_scale || 5)"
                          :key="n"
                          type="button"
                          class="score-btn"
                          :class="{ active: scores[item.id]?.self_score === n }"
                          :disabled="appraisal.self_score_submitted || !canSubmitSelfScore"
                          @click="setScore(item.id, 'self_score', n)"
                        >
                          {{ n }}
                        </button>
                      </div>
                    </div>

                    <div class="form-group">
                      <label>Comment</label>
                      <b-form-textarea
                        v-model="scores[item.id].self_comment"
                        rows="2"
                        placeholder="Add your comment..."
                        :disabled="appraisal.self_score_submitted || !canSubmitSelfScore"
                        class="form-control-modern"
                      ></b-form-textarea>
                    </div>
                  </div>
                </div>

                <!-- Manager Evaluation (hidden from employees for privacy) -->
                <div v-if="canViewManagerScores && (canSubmitManagerScore || appraisal.manager_score_submitted)" class="scoring-column manager">
                  <div class="scoring-header">
                    <i class="fa fa-user-tie"></i>
                    <span>Manager Evaluation</span>
                    <span v-if="appraisal.manager_score_submitted" class="submitted-badge">
                      <i class="fa fa-check-circle"></i> Submitted
                    </span>
                  </div>
                  
                  <div class="scoring-body">
                    <template v-if="item.target_type && item.target_type !== 'score'">
                      <div class="form-group">
                        <label>Target: <strong>{{ formatTargetValue(item) }}</strong></label>
                        <div v-if="scores[item.id].actual_value" class="target-display mb-2">
                          <small class="text-muted">Employee reported:</small>
                          <span class="actual-value">{{ scores[item.id].actual_value }}</span>
                        </div>
                        <b-form-input
                          v-model="scores[item.id].manager_actual_value"
                          type="number"
                          :placeholder="'Enter your assessed ' + getTargetLabel(item.target_type)"
                          :disabled="appraisal.manager_score_submitted || !canSubmitManagerScore"
                          class="form-control-modern"
                        ></b-form-input>
                        <small class="text-muted">Your assessment of the actual value</small>
                      </div>
                      <div v-if="scores[item.id].manager_actual_value" class="form-group">
                        <label>Achievement (Manager's Assessment)</label>
                        <div class="achievement-display">
                          <span class="achievement-value" :class="getManagerAchievementClass(item)">
                            {{ calculateManagerAchievement(item) }}%
                          </span>
                          <small class="text-muted ml-2">({{ scores[item.id].manager_actual_value }} / {{ item.target_value }})</small>
                        </div>
                      </div>
                    </template>

                    <div v-if="!item.target_type || item.target_type === 'score'" class="form-group">
                      <label>Score</label>
                      <div class="score-selector">
                        <button
                          v-for="n in parseInt(appraisal.score_scale || 5)"
                          :key="n"
                          type="button"
                          class="score-btn manager-btn"
                          :class="{ active: scores[item.id]?.manager_score === n }"
                          :disabled="appraisal.manager_score_submitted || !canSubmitManagerScore"
                          @click="setScore(item.id, 'manager_score', n)"
                        >
                          {{ n }}
                        </button>
                      </div>
                    </div>

                    <div class="form-group">
                      <label>Comment</label>
                      <b-form-textarea
                        v-model="scores[item.id].manager_comment"
                        rows="2"
                        placeholder="Add your feedback..."
                        :disabled="appraisal.manager_score_submitted || !canSubmitManagerScore"
                        class="form-control-modern"
                      ></b-form-textarea>
                    </div>
                  </div>
                </div>

                <!-- Reviewer Evaluation (hidden from employees/managers for privacy) -->
                <div v-if="showReviewerColumn && canViewReviewerScores" class="scoring-column reviewer">
                  <div class="scoring-header">
                    <i class="fa fa-user-check"></i>
                    <span>{{ appraisal.is_disputed ? 'Dispute Resolution' : 'Reviewer Evaluation' }}</span>
                    <span v-if="appraisal.reviewer_score_submitted" class="submitted-badge">
                      <i class="fa fa-check-circle"></i> Submitted
                    </span>
                  </div>
                  
                  <div class="scoring-body">
                    <div v-if="canSubmitReviewerScore && !appraisal.reviewer_score_submitted" class="scores-comparison mb-3">
                      <div class="comparison-item">
                        <span class="comparison-label">Self Score:</span>
                        <span class="comparison-value">{{ item.self_score || '-' }}</span>
                      </div>
                      <div class="comparison-item">
                        <span class="comparison-label">Manager Score:</span>
                        <span class="comparison-value">{{ item.manager_score || '-' }}</span>
                      </div>
                    </div>

                    <div class="form-group">
                      <label>{{ appraisal.is_disputed ? 'Final Ruling Score (Required)' : 'Score' }}</label>
                      <div class="score-selector">
                        <button
                          v-for="n in parseInt(appraisal.score_scale || 5)"
                          :key="n"
                          type="button"
                          class="score-btn reviewer-btn"
                          :class="{ active: scores[item.id]?.reviewer_score === n }"
                          :disabled="appraisal.reviewer_score_submitted || !canSubmitReviewerScore"
                          @click="setScore(item.id, 'reviewer_score', n)"
                        >
                          {{ n }}
                        </button>
                      </div>
                    </div>

                    <div class="form-group">
                      <label>Comment</label>
                      <b-form-textarea
                        v-model="scores[item.id].reviewer_comment"
                        rows="2"
                        placeholder="Add your feedback..."
                        :disabled="appraisal.reviewer_score_submitted || !canSubmitReviewerScore"
                        class="form-control-modern"
                      ></b-form-textarea>
                    </div>
                  </div>
                </div>
              </div>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="action-bar">
            <div class="action-info">
              <span v-if="!appraisal.self_score_submitted && canSubmitSelfScore" class="action-hint">
                <i class="fa fa-info-circle"></i> Complete your self assessment and submit
              </span>
              <span v-else-if="!appraisal.manager_score_submitted && canSubmitManagerScore" class="action-hint">
                <i class="fa fa-info-circle"></i> Review and submit your evaluation
              </span>
              <span v-else-if="!appraisal.reviewer_score_submitted && canSubmitReviewerScore" class="action-hint">
                <i class="fa fa-info-circle"></i> Review and submit your independent evaluation
              </span>
            </div>
            <div class="action-buttons">
              <b-button
                v-if="canSubmitSelfScore && !appraisal.self_score_submitted"
                variant="outline-secondary"
                size="lg"
                class="btn-draft mr-2"
                :disabled="savingDraft"
                @click="saveSelfScoreDraft"
              >
                <b-spinner small v-if="savingDraft" class="mr-2"></b-spinner>
                <i v-else class="fa fa-save mr-2"></i>
                Save Draft
              </b-button>
              <b-button
                v-if="canSubmitSelfScore && !appraisal.self_score_submitted"
                variant="primary"
                size="lg"
                class="btn-submit"
                :disabled="submitting"
                @click="submitSelfScore"
              >
                <b-spinner small v-if="submitting" class="mr-2"></b-spinner>
                <i v-else class="fa fa-paper-plane mr-2"></i>
                Submit Self Assessment
              </b-button>

              <b-button
                v-if="canSubmitManagerScore && !appraisal.manager_score_submitted"
                variant="outline-secondary"
                size="lg"
                class="btn-draft mr-2"
                :disabled="savingDraft"
                @click="saveManagerScoreDraft"
              >
                <b-spinner small v-if="savingDraft" class="mr-2"></b-spinner>
                <i v-else class="fa fa-save mr-2"></i>
                Save Draft
              </b-button>
              <b-button
                v-if="canSubmitManagerScore && !appraisal.manager_score_submitted"
                variant="success"
                size="lg"
                class="btn-submit"
                :disabled="submitting"
                @click="submitManagerScore"
              >
                <b-spinner small v-if="submitting" class="mr-2"></b-spinner>
                <i v-else class="fa fa-check-double mr-2"></i>
                Submit Manager Evaluation
              </b-button>

              <b-button
                v-if="canSubmitReviewerScore && !appraisal.reviewer_score_submitted"
                variant="outline-secondary"
                size="lg"
                class="btn-draft mr-2"
                :disabled="savingDraft"
                @click="saveReviewerScoreDraft"
              >
                <b-spinner small v-if="savingDraft" class="mr-2"></b-spinner>
                <i v-else class="fa fa-save mr-2"></i>
                Save Draft
              </b-button>
              <b-button
                v-if="canSubmitReviewerScore && !appraisal.reviewer_score_submitted"
                variant="info"
                size="lg"
                class="btn-submit"
                :disabled="submitting"
                @click="submitReviewerScore"
              >
                <b-spinner small v-if="submitting" class="mr-2"></b-spinner>
                <i v-else class="fa fa-user-check mr-2"></i>
                {{ appraisal.is_disputed ? 'Submit Dispute Resolution' : 'Submit Reviewer Evaluation' }}
              </b-button>

              <b-button
                v-if="canRaiseDispute"
                variant="warning"
                size="lg"
                class="btn-submit"
                :disabled="submitting"
                @click="showDisputeModal = true"
              >
                <i class="fa fa-exclamation-triangle mr-2"></i>
                Raise Dispute
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </b-overlay>

    <!-- Dispute Modal -->
    <b-modal v-model="showDisputeModal" title="Raise Dispute" @ok="raiseDispute" ok-title="Submit Dispute" ok-variant="warning">
      <p>Raising a dispute will require the independent reviewer to make the final ruling on this appraisal.</p>
      
      <b-form-group v-if="appraisal && !appraisal.reviewer" label="Select Reviewer *" label-for="dispute-reviewer">
        <b-form-select
          id="dispute-reviewer"
          v-model="disputeReviewerId"
          :options="reviewerOptions"
          required
        >
          <template #first>
            <b-form-select-option :value="null" disabled>-- Select a reviewer --</b-form-select-option>
          </template>
        </b-form-select>
        <small class="text-muted">A reviewer is required to resolve the dispute.</small>
      </b-form-group>

      <b-form-group label="Reason for Dispute *" label-for="dispute-reason">
        <b-form-textarea
          id="dispute-reason"
          v-model="disputeReason"
          rows="3"
          placeholder="Please explain why you are disputing this appraisal..."
          required
        ></b-form-textarea>
      </b-form-group>
    </b-modal>

    <!-- Add Criteria Modal -->
    <b-modal
      v-model="showAddCriteriaModal"
      title="Add Criteria"
      size="lg"
      @ok="submitAddCriteria"
      @show="initExistingCriteriaWeights"
      :ok-disabled="!newCriteria.name || addingCriteria"
      ok-title="Save Changes"
      ok-variant="primary"
    >
      <!-- Existing Criteria Weights Section -->
      <div v-if="existingCriteriaWeights.length > 0" class="existing-criteria-section mb-4">
        <h6 class="section-label">
          <i class="fa fa-list-ol mr-2"></i>Adjust Existing Criteria Weights
        </h6>
        <div class="criteria-weights-list">
          <div
            v-for="item in existingCriteriaWeights"
            :key="item.id"
            class="criteria-weight-row"
            :class="{ 'is-bonus': item.is_bonus }"
          >
            <div class="criteria-weight-name">
              <span>{{ item.name }}</span>
              <small v-if="item.is_bonus" class="text-warning ml-1">(Bonus)</small>
            </div>
            <div class="criteria-weight-input">
              <b-form-input
                v-model.number="item.weight"
                type="number"
                min="0"
                max="100"
                size="sm"
                :disabled="item.is_bonus"
                class="weight-input"
              ></b-form-input>
              <span class="weight-suffix">%</span>
            </div>
          </div>
        </div>
      </div>

      <!-- New Criteria Section -->
      <div class="new-criteria-section">
        <h6 class="section-label">
          <i class="fa fa-plus-circle mr-2"></i>New Criteria Details
        </h6>

        <b-form-group label="Criteria Name *" label-for="criteria-name">
          <b-form-input
            id="criteria-name"
            v-model="newCriteria.name"
            placeholder="Enter criteria name"
            required
          ></b-form-input>
        </b-form-group>

        <b-form-group label="Description" label-for="criteria-description">
          <b-form-textarea
            id="criteria-description"
            v-model="newCriteria.description"
            rows="2"
            placeholder="Optional description"
          ></b-form-textarea>
        </b-form-group>

        <div class="row">
          <div class="col-6">
            <b-form-group label="Weight (%)" label-for="criteria-weight">
              <b-form-input
                id="criteria-weight"
                v-model.number="newCriteria.weight"
                type="number"
                min="0"
                max="100"
                placeholder="0"
              ></b-form-input>
            </b-form-group>
          </div>
          <div class="col-6">
            <b-form-group label="Target Type" label-for="criteria-target-type">
              <b-form-select
                id="criteria-target-type"
                v-model="newCriteria.target_type"
                :options="targetTypeOptions"
              ></b-form-select>
            </b-form-group>
          </div>
        </div>

        <b-form-group
          v-if="newCriteria.target_type && newCriteria.target_type !== 'score'"
          label="Target Value"
          label-for="criteria-target-value"
        >
          <b-form-input
            id="criteria-target-value"
            v-model.number="newCriteria.target_value"
            type="number"
            :placeholder="'Enter target ' + getTargetLabel(newCriteria.target_type)"
          ></b-form-input>
        </b-form-group>

        <b-form-checkbox v-model="newCriteria.is_bonus">
          Bonus Criteria (does not count toward 100% weight)
        </b-form-checkbox>
      </div>

      <!-- Weight Total Summary -->
      <div class="weight-summary mt-3" :class="{ 'is-valid': totalWeightWithNew === 100, 'is-invalid': totalWeightWithNew !== 100 }">
        <div class="weight-summary-row">
          <span>Existing Criteria:</span>
          <span>{{ existingNonBonusWeight }}%</span>
        </div>
        <div class="weight-summary-row" v-if="!newCriteria.is_bonus">
          <span>New Criteria:</span>
          <span>{{ newCriteria.weight || 0 }}%</span>
        </div>
        <div class="weight-summary-row total">
          <span>Total Weight:</span>
          <span :class="{ 'text-success': totalWeightWithNew === 100, 'text-danger': totalWeightWithNew !== 100 }">
            {{ totalWeightWithNew }}%
          </span>
        </div>
        <small v-if="totalWeightWithNew !== 100" class="text-danger">
          <i class="fa fa-exclamation-triangle mr-1"></i>
          Non-bonus weights should total 100%
        </small>
      </div>
    </b-modal>

    <!-- Edit Criteria Modal -->
    <b-modal
      v-model="showEditCriteriaModal"
      title="Edit Criteria"
      @ok="submitEditCriteria"
      :ok-disabled="!editCriteria.name || editingCriteria"
      ok-title="Save Changes"
      ok-variant="primary"
    >
      <b-form-group label="Criteria Name *" label-for="edit-criteria-name">
        <b-form-input
          id="edit-criteria-name"
          v-model="editCriteria.name"
          placeholder="Enter criteria name"
          required
        ></b-form-input>
      </b-form-group>

      <b-form-group label="Description" label-for="edit-criteria-description">
        <b-form-textarea
          id="edit-criteria-description"
          v-model="editCriteria.description"
          rows="2"
          placeholder="Optional description"
        ></b-form-textarea>
      </b-form-group>

      <div class="row">
        <div class="col-6">
          <b-form-group label="Weight (%)" label-for="edit-criteria-weight">
            <b-form-input
              id="edit-criteria-weight"
              v-model.number="editCriteria.weight"
              type="number"
              min="0"
              max="100"
              placeholder="0"
            ></b-form-input>
          </b-form-group>
        </div>
        <div class="col-6">
          <b-form-group label="Target Type" label-for="edit-criteria-target-type">
            <b-form-select
              id="edit-criteria-target-type"
              v-model="editCriteria.target_type"
              :options="targetTypeOptions"
            ></b-form-select>
          </b-form-group>
        </div>
      </div>

      <b-form-group
        v-if="editCriteria.target_type && editCriteria.target_type !== 'score'"
        label="Target Value"
        label-for="edit-criteria-target-value"
      >
        <b-form-input
          id="edit-criteria-target-value"
          v-model.number="editCriteria.target_value"
          type="number"
          :placeholder="'Enter target ' + getTargetLabel(editCriteria.target_type)"
        ></b-form-input>
      </b-form-group>

      <b-form-checkbox v-model="editCriteria.is_bonus">
        Bonus Criteria (does not count toward 100% weight)
      </b-form-checkbox>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'AppraisalDetail',
  data() {
    return {
      loading: false,
      submitting: false,
      savingDraft: false,
      scores: {},
      showDisputeModal: false,
      disputeReason: '',
      disputeReviewerId: null,
      showAddCriteriaModal: false,
      addingCriteria: false,
      newCriteria: {
        name: '',
        description: '',
        weight: 0,
        target_type: 'score',
        target_value: null,
        is_bonus: false
      },
      existingCriteriaWeights: [],
      showEditCriteriaModal: false,
      editingCriteria: false,
      editCriteria: {
        id: null,
        name: '',
        description: '',
        weight: 0,
        target_type: 'score',
        target_value: null,
        is_bonus: false
      },
      targetTypeOptions: [
        { value: 'score', text: 'Score (1-5 or 1-10)' },
        { value: 'percentage', text: 'Percentage (0-100%)' },
        { value: 'number', text: 'Number (units, count)' },
        { value: 'currency', text: 'Currency (amount)' }
      ]
    };
  },
  computed: {
    ...mapGetters('appraisal', ['currentAppraisal']),
    ...mapGetters('users', { users: 'all_admin_users' }),
    
    appraisal() {
      return this.currentAppraisal;
    },
    
    canSubmitSelfScore() {
      return this.appraisal?.role === 'employee' || this.appraisal?.role === 'both';
    },
    
    canSubmitManagerScore() {
      return this.appraisal?.role === 'manager' || this.appraisal?.role === 'both';
    },

    canViewManagerScores() {
      // Employees can see manager scores AFTER appraisal is completed (for transparency/feedback)
      // During scoring process, only managers/reviewers can see
      const isCompleted = this.appraisal?.final_score !== null && this.appraisal?.final_score !== undefined;
      return isCompleted || this.appraisal?.role === 'manager' || this.appraisal?.role === 'both' || this.appraisal?.role === 'reviewer';
    },

    canViewReviewerScores() {
      // Employees can see reviewer scores AFTER appraisal is completed (for transparency/feedback)
      // During scoring process, only reviewers can see
      const isCompleted = this.appraisal?.final_score !== null && this.appraisal?.final_score !== undefined;
      return isCompleted || this.appraisal?.role === 'reviewer';
    },

    canSubmitReviewerScore() {
      return this.appraisal?.role === 'reviewer' || this.appraisal?.can_act_as_reviewer;
    },

    isReviewerOnlyMode() {
      const selfWeight = parseInt(this.appraisal?.self_score_weight) || 0;
      const managerWeight = parseInt(this.appraisal?.manager_score_weight) || 0;
      return selfWeight === 0 && managerWeight === 0;
    },

    showReviewerColumn() {
      if (!this.appraisal) return false;
      if (this.appraisal.reviewer_score_submitted) return true;
      if (this.canSubmitReviewerScore && this.appraisal.enable_independent_reviewer) return true;
      if (this.isReviewerOnlyMode && this.appraisal.reviewer) return true;
      return false;
    },

    canRaiseDispute() {
      if (!this.appraisal) return false;
      if (this.appraisal.is_disputed) return false;
      if (!this.appraisal.manager_score_submitted) return false;
      return this.appraisal.role === 'employee' || this.appraisal.role === 'manager' || this.appraisal.role === 'both';
    },

    canAddCriteria() {
      if (!this.appraisal) return false;
      if (this.appraisal.final_score !== null) return false;
      const isEmployee = this.appraisal.role === 'employee' || this.appraisal.role === 'both';
      const isManager = this.appraisal.role === 'manager' || this.appraisal.role === 'both';
      const hasAdminPermission = this.$store.getters.user_permissions?.includes('manage.appraisal.cards');
      return isEmployee || isManager || hasAdminPermission;
    },

    canEditCriteria() {
      // Same permissions as canAddCriteria
      return this.canAddCriteria;
    },

    existingNonBonusWeight() {
      return this.existingCriteriaWeights
        .filter(c => !c.is_bonus)
        .reduce((sum, c) => sum + (parseFloat(c.weight) || 0), 0);
    },

    totalWeightWithNew() {
      const newWeight = this.newCriteria.is_bonus ? 0 : (parseFloat(this.newCriteria.weight) || 0);
      return this.existingNonBonusWeight + newWeight;
    },

    reviewerOptions() {
      if (!this.appraisal || !this.users) return [];
      const excludeIds = [
        this.appraisal.appraised_user?.id,
        this.appraisal.manager?.id
      ].filter(Boolean);
      
      return (this.users || [])
        .filter(u => !excludeIds.includes(u.id))
        .map(u => ({
          value: u.id,
          text: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.name
        }));
    },
    
    scoreClass() {
      if (this.appraisal?.final_score === null) return 'pending';
      if (this.appraisal?.final_score >= 80) return 'excellent';
      if (this.appraisal?.final_score >= 60) return 'good';
      if (this.appraisal?.final_score >= 40) return 'average';
      return 'needs-improvement';
    },

    groupedCriteria() {
      if (!this.appraisal?.criteria) return [];

      const groups = {};
      let globalIndex = 0;

      this.appraisal.criteria.forEach(item => {
        const category = item.category || 'General';
        if (!groups[category]) {
          groups[category] = {
            name: category,
            items: []
          };
        }
        groups[category].items.push({
          ...item,
          globalIndex: ++globalIndex
        });
      });

      return Object.values(groups);
    },
    
    formatPeriod() {
      if (this.appraisal?.start_date && this.appraisal?.end_date) {
        const start = this.formatDate(this.appraisal.start_date);
        const end = this.formatDate(this.appraisal.end_date);
        return `${start} - ${end}`;
      }
      if (this.appraisal?.start_date) {
        return this.formatDate(this.appraisal.start_date);
      }
      if (this.appraisal?.period) {
        const period = this.appraisal.period;
        if (period.match(/^\d{4}-\d{2}$/)) {
          const [year, month] = period.split('-');
          const date = new Date(year, month - 1);
          return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        }
        return period;
      }
      return '-';
    }
  },
  watch: {
    currentAppraisal: {
      handler(val) {
        if (val?.criteria) {
          this.initializeScores();
        }
      },
      immediate: true
    }
  },
  mounted() {
    this.loadAppraisal();
    this.fetchUsers();
  },
  methods: {
    ...mapActions('appraisal', [
      'fetchMyAppraisalById',
      'submitMyAppraisalSelfScore',
      'submitMyAppraisalManagerScore',
      'submitMyAppraisalReviewerScore',
      'raiseMyAppraisalDispute',
      'addCriteriaToAppraisal',
      'updateCriteria',
      'deleteCriteria'
    ]),
    ...mapActions('users', { fetchUsers: 'fetchAll' }),
    
    async loadAppraisal() {
      this.loading = true;
      try {
        await this.fetchMyAppraisalById(this.$route.params.id);
      } finally {
        this.loading = false;
      }
    },

    initializeScores() {
      const newScores = {};
      (this.appraisal?.criteria || []).forEach(item => {
        newScores[item.id] = {
          item_id: item.id,
          self_score: item.self_score ? parseInt(item.self_score) : null,
          self_comment: item.self_comment || '',
          manager_score: item.manager_score ? parseInt(item.manager_score) : null,
          manager_comment: item.manager_comment || '',
          reviewer_score: item.reviewer_score ? parseInt(item.reviewer_score) : null,
          reviewer_comment: item.reviewer_comment || '',
          actual_value: item.actual_value ? parseFloat(item.actual_value) : null,
          manager_actual_value: item.manager_actual_value ? parseFloat(item.manager_actual_value) : null
        };
      });
      this.scores = newScores;
    },

    setScore(checkoffId, field, value) {
      if (this.scores[checkoffId]) {
        this.scores[checkoffId][field] = value;
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },

    formatTargetValue(item) {
      const value = item.target_value;
      if (!value) return '-';
      switch (item.target_type) {
        case 'currency':
          return '$' + Number(value).toLocaleString();
        case 'percentage':
          return value + '%';
        default:
          return Number(value).toLocaleString();
      }
    },

    getTargetLabel(targetType) {
      switch (targetType) {
        case 'currency': return 'amount';
        case 'percentage': return 'percentage';
        case 'number': return 'value';
        default: return 'value';
      }
    },

    calculateAchievement(item) {
      const actual = parseFloat(this.scores[item.id]?.actual_value);
      const target = parseFloat(item.target_value);
      if (isNaN(actual) || isNaN(target) || target === 0) return 0;
      return Math.round((actual / target) * 100);
    },

    getAchievementClass(item) {
      const achievement = this.calculateAchievement(item);
      if (achievement >= 100) return 'achievement-excellent';
      if (achievement >= 80) return 'achievement-good';
      if (achievement >= 60) return 'achievement-average';
      return 'achievement-low';
    },

    calculateManagerAchievement(item) {
      const actual = parseFloat(this.scores[item.id]?.manager_actual_value);
      const target = parseFloat(item.target_value);
      if (isNaN(actual) || isNaN(target) || target === 0) return 0;
      return Math.round((actual / target) * 100);
    },

    getManagerAchievementClass(item) {
      const achievement = this.calculateManagerAchievement(item);
      if (achievement >= 100) return 'achievement-excellent';
      if (achievement >= 80) return 'achievement-good';
      if (achievement >= 60) return 'achievement-average';
      return 'achievement-low';
    },

    getAttainedWeight(item) {
      const scoreData = this.scores[item.id];
      if (!scoreData) return null;

      const weight = parseFloat(item.weight) || 0;
      const maxScore = parseInt(this.appraisal?.score_scale || 5);

      // Get score weightings from the appraisal template
      const selfWeight = parseFloat(this.appraisal?.self_score_weight || 0) / 100;
      const managerWeight = parseFloat(this.appraisal?.manager_score_weight || 100) / 100;

      // Check if required scores are submitted based on weightings
      const selfSubmitted = this.appraisal?.self_score_submitted;
      const managerSubmitted = this.appraisal?.manager_score_submitted;

      // If self weight > 0 but self not submitted, don't show attained
      // If manager weight > 0 but manager not submitted, don't show attained
      // We need ALL weighted evaluators to have submitted to show the final attained weight
      if (selfWeight > 0 && !selfSubmitted) return null;
      if (managerWeight > 0 && !managerSubmitted) return null;

      // For target-based criteria, use weighted achievement percentage
      if (item.target_type && item.target_type !== 'score') {
        const target = parseFloat(item.target_value);
        if (isNaN(target) || target === 0) return null;

        const selfActual = parseFloat(scoreData.actual_value);
        const managerActual = parseFloat(scoreData.manager_actual_value);

        let weightedAchievement = 0;
        let totalAchievementWeight = 0;

        // Employee's reported value (weighted by self_score_weight)
        if (!isNaN(selfActual) && selfWeight > 0) {
          const selfAchievement = Math.min((selfActual / target) * 100, 100) / 100;
          weightedAchievement += selfAchievement * selfWeight;
          totalAchievementWeight += selfWeight;
        }

        // Manager's assessed value (weighted by manager_score_weight)
        if (!isNaN(managerActual) && managerWeight > 0) {
          const managerAchievement = Math.min((managerActual / target) * 100, 100) / 100;
          weightedAchievement += managerAchievement * managerWeight;
          totalAchievementWeight += managerWeight;
        }

        if (totalAchievementWeight === 0) return null;

        const finalAchievement = weightedAchievement / totalAchievementWeight;
        return weight * finalAchievement;
      }

      // For score-based criteria, calculate based on scores entered
      const selfScore = parseFloat(scoreData.self_score);
      const managerScore = parseFloat(scoreData.manager_score);

      // Need at least one score to show attained weight
      if (isNaN(selfScore) && isNaN(managerScore)) return null;

      let weightedScore = 0;
      let totalWeight = 0;

      if (!isNaN(selfScore) && selfWeight > 0) {
        weightedScore += (selfScore / maxScore) * selfWeight;
        totalWeight += selfWeight;
      }

      if (!isNaN(managerScore) && managerWeight > 0) {
        weightedScore += (managerScore / maxScore) * managerWeight;
        totalWeight += managerWeight;
      }

      if (totalWeight === 0) return null;

      // Calculate the attained percentage of the weight
      const attainedPercentage = weightedScore / totalWeight;
      return weight * attainedPercentage;
    },

    getAttainedWeightClass(item) {
      const attained = this.getAttainedWeight(item);
      const weight = parseFloat(item.weight) || 0;
      if (attained === null || weight === 0) return '';

      const percentage = (attained / weight) * 100;
      if (percentage >= 90) return 'attained-excellent';
      if (percentage >= 70) return 'attained-good';
      if (percentage >= 50) return 'attained-average';
      return 'attained-low';
    },

    async submitSelfScore() {
      this.submitting = true;
      try {
        const scoresArray = Object.values(this.scores).map(s => ({
          item_id: s.item_id,
          score: s.self_score,
          comment: s.self_comment,
          actual_value: s.actual_value
        }));

        await this.submitMyAppraisalSelfScore({
          id: this.appraisal.id,
          scores: scoresArray
        });

        this.$bvToast.toast('Self assessment submitted successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });

        await this.loadAppraisal();
      } catch (error) {
        this.$bvToast.toast(error.message || 'Failed to submit self assessment', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.submitting = false;
      }
    },

    async submitManagerScore() {
      this.submitting = true;
      try {
        const scoresArray = Object.values(this.scores).map(s => ({
          item_id: s.item_id,
          score: s.manager_score,
          comment: s.manager_comment,
          manager_actual_value: s.manager_actual_value
        }));

        await this.submitMyAppraisalManagerScore({
          id: this.appraisal.id,
          scores: scoresArray
        });

        this.$bvToast.toast('Manager evaluation submitted successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });

        await this.loadAppraisal();
      } catch (error) {
        this.$bvToast.toast(error.message || 'Failed to submit manager evaluation', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.submitting = false;
      }
    },

    async submitReviewerScore() {
      this.submitting = true;
      try {
        const scoresArray = Object.values(this.scores).map(s => ({
          item_id: s.item_id,
          score: s.reviewer_score,
          comment: s.reviewer_comment
        }));

        await this.submitMyAppraisalReviewerScore({
          id: this.appraisal.id,
          scores: scoresArray
        });

        this.$bvToast.toast('Reviewer evaluation submitted successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });

        await this.loadAppraisal();
      } catch (error) {
        this.$bvToast.toast(error.message || 'Failed to submit reviewer evaluation', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.submitting = false;
      }
    },

    async saveSelfScoreDraft() {
      this.savingDraft = true;
      try {
        const scoresArray = Object.values(this.scores).map(s => ({
          item_id: s.item_id,
          score: s.self_score,
          comment: s.self_comment,
          actual_value: s.actual_value
        }));

        await this.$store.dispatch('appraisal/saveSelfScoreDraft', {
          id: this.appraisal.id,
          scores: scoresArray
        });

        this.$bvToast.toast('Draft saved successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        this.$bvToast.toast(error.message || 'Failed to save draft', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.savingDraft = false;
      }
    },

    async saveManagerScoreDraft() {
      this.savingDraft = true;
      try {
        const scoresArray = Object.values(this.scores).map(s => ({
          item_id: s.item_id,
          score: s.manager_score,
          comment: s.manager_comment,
          manager_actual_value: s.manager_actual_value
        }));

        await this.$store.dispatch('appraisal/saveManagerScoreDraft', {
          id: this.appraisal.id,
          scores: scoresArray
        });

        this.$bvToast.toast('Draft saved successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        this.$bvToast.toast(error.message || 'Failed to save draft', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.savingDraft = false;
      }
    },

    async saveReviewerScoreDraft() {
      this.savingDraft = true;
      try {
        const scoresArray = Object.values(this.scores).map(s => ({
          item_id: s.item_id,
          score: s.reviewer_score,
          comment: s.reviewer_comment
        }));

        await this.$store.dispatch('appraisal/saveReviewerScoreDraft', {
          id: this.appraisal.id,
          scores: scoresArray
        });

        this.$bvToast.toast('Draft saved successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        this.$bvToast.toast(error.message || 'Failed to save draft', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.savingDraft = false;
      }
    },

    async raiseDispute(bvModalEvt) {
      bvModalEvt.preventDefault();
      if (!this.disputeReason.trim()) {
        this.$bvToast.toast('Please provide a reason for the dispute', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
        return;
      }

      if (!this.appraisal.reviewer && !this.disputeReviewerId) {
        this.$bvToast.toast('Please select a reviewer to handle the dispute', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
        return;
      }

      this.submitting = true;
      try {
        const payload = {
          id: this.appraisal.id,
          reason: this.disputeReason
        };
        if (!this.appraisal.reviewer && this.disputeReviewerId) {
          payload.reviewer_id = this.disputeReviewerId;
        }

        await this.raiseMyAppraisalDispute(payload);

        this.$bvToast.toast('Dispute raised successfully. The independent reviewer will make the final ruling.', {
          title: 'Success',
          variant: 'success',
          solid: true
        });

        this.showDisputeModal = false;
        this.disputeReason = '';
        this.disputeReviewerId = null;
        await this.loadAppraisal();
      } catch (error) {
        this.$bvToast.toast(error.message || 'Failed to raise dispute', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.submitting = false;
      }
    },

    initExistingCriteriaWeights() {
      if (!this.appraisal?.criteria) {
        this.existingCriteriaWeights = [];
        return;
      }
      this.existingCriteriaWeights = this.appraisal.criteria.map(c => ({
        id: c.id,
        name: c.name,
        weight: parseFloat(c.weight) || 0,
        originalWeight: parseFloat(c.weight) || 0,
        is_bonus: c.is_bonus || false
      }));
    },

    async submitAddCriteria(bvModalEvt) {
      bvModalEvt.preventDefault();

      if (!this.newCriteria.name) {
        this.$bvToast.toast('Criteria name is required', {
          title: 'Validation Error',
          variant: 'warning',
          solid: true
        });
        return;
      }

      this.addingCriteria = true;
      try {
        // First, update any changed weights on existing criteria
        const weightUpdates = this.existingCriteriaWeights.filter(
          c => c.weight !== c.originalWeight && !c.is_bonus
        );

        for (const item of weightUpdates) {
          await this.updateCriteria({
            appraisalId: this.appraisal.id,
            criteriaId: item.id,
            data: { weight: item.weight }
          });
        }

        // Then add the new criteria
        await this.addCriteriaToAppraisal({
          id: this.appraisal.id,
          criteria: this.newCriteria
        });

        this.$bvToast.toast('Criteria added successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });

        this.showAddCriteriaModal = false;
        this.resetNewCriteria();
        this.existingCriteriaWeights = [];
        await this.loadAppraisal();
      } catch (error) {
        this.$bvToast.toast(error.response?.data?.error?.message || 'Failed to add criteria', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.addingCriteria = false;
      }
    },

    resetNewCriteria() {
      this.newCriteria = {
        name: '',
        description: '',
        weight: 0,
        target_type: 'score',
        target_value: null,
        is_bonus: false
      };
    },

    openEditCriteriaModal(item) {
      this.editCriteria = {
        id: item.id,
        name: item.name,
        description: item.description || '',
        weight: parseFloat(item.weight) || 0,
        target_type: item.target_type || 'score',
        target_value: item.target_value || null,
        is_bonus: item.is_bonus || false
      };
      this.showEditCriteriaModal = true;
    },

    async submitEditCriteria(bvModalEvt) {
      bvModalEvt.preventDefault();

      if (!this.editCriteria.name) {
        this.$bvToast.toast('Criteria name is required', {
          title: 'Validation Error',
          variant: 'warning',
          solid: true
        });
        return;
      }

      this.editingCriteria = true;
      try {
        await this.updateCriteria({
          appraisalId: this.appraisal.id,
          criteriaId: this.editCriteria.id,
          data: {
            name: this.editCriteria.name,
            description: this.editCriteria.description,
            weight: this.editCriteria.weight,
            target_type: this.editCriteria.target_type,
            target_value: this.editCriteria.target_value,
            is_bonus: this.editCriteria.is_bonus
          }
        });

        this.$bvToast.toast('Criteria updated successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });

        this.showEditCriteriaModal = false;
        await this.loadAppraisal();
      } catch (error) {
        this.$bvToast.toast(error.response?.data?.error?.message || 'Failed to update criteria', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.editingCriteria = false;
      }
    },

    async confirmDeleteCriteria(item) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Are you sure you want to delete "${item.name}"? This action cannot be undone.`,
        {
          title: 'Delete Criteria',
          okVariant: 'danger',
          okTitle: 'Delete',
          cancelTitle: 'Cancel',
          centered: true
        }
      );

      if (confirmed) {
        try {
          await this.deleteCriteria({
            appraisalId: this.appraisal.id,
            criteriaId: item.id
          });

          this.$bvToast.toast('Criteria deleted successfully', {
            title: 'Success',
            variant: 'success',
            solid: true
          });

          await this.loadAppraisal();
        } catch (error) {
          this.$bvToast.toast(error.response?.data?.error?.message || 'Failed to delete criteria', {
            title: 'Error',
            variant: 'danger',
            solid: true
          });
        }
      }
    },

    printAppraisal() {
      const printWindow = window.open('', '_blank');
      const reportContent = this.generatePrintReport();

      printWindow.document.write(reportContent);
      printWindow.document.close();

      // Wait for content to load then print
      printWindow.onload = function() {
        printWindow.print();
      };
    },

    generatePrintReport() {
      const appraisal = this.appraisal;
      const scores = this.scores;

      // Build criteria rows
      let criteriaRows = '';
      let criteriaIndex = 1;

      this.groupedCriteria.forEach(group => {
        criteriaRows += `
          <tr>
            <td colspan="6" class="category-header">${group.name}</td>
          </tr>
        `;

        group.items.forEach(item => {
          const scoreData = scores[item.id] || {};
          const selfScore = scoreData.self_score || '-';
          const managerScore = scoreData.manager_score || '-';
          const reviewerScore = scoreData.reviewer_score || '-';
          const selfComment = scoreData.self_comment || '';
          const managerComment = scoreData.manager_comment || '';
          const hasComments = selfComment || managerComment;

          criteriaRows += `
            <tr class="criteria-row">
              <td class="center">${criteriaIndex}</td>
              <td class="criteria-cell">
                <div class="criteria-name">${item.name}</div>
                ${item.description ? `<div class="criteria-desc">${item.description}</div>` : ''}
              </td>
              <td class="center">${item.weight}%${item.is_bonus ? '<br><span class="bonus-tag">Bonus</span>' : ''}</td>
              <td class="center score-cell">${selfScore}</td>
              <td class="center score-cell">${managerScore}</td>
              <td class="center score-cell">${reviewerScore}</td>
            </tr>
            ${hasComments ? `
            <tr class="comments-row">
              <td></td>
              <td colspan="5" class="comments-cell">
                ${selfComment ? `<div class="comment-item"><span class="comment-label">Self:</span> ${selfComment}</div>` : ''}
                ${managerComment ? `<div class="comment-item"><span class="comment-label">Manager:</span> ${managerComment}</div>` : ''}
              </td>
            </tr>
            ` : ''}
          `;
          criteriaIndex++;
        });
      });

      // Dispute section
      let disputeSection = '';
      if (appraisal.is_disputed) {
        const isResolved = appraisal.dispute_resolved || appraisal.reviewer_score_submitted;
        disputeSection = `
          <div class="dispute-box ${isResolved ? 'resolved' : ''}">
            <div class="dispute-title">${isResolved ? '✓ Dispute Resolved' : '⚠ Appraisal Under Dispute'}</div>
            ${appraisal.dispute_reason ? `<div class="dispute-reason">Reason: ${appraisal.dispute_reason}</div>` : ''}
          </div>
        `;
      }

      // Score status
      const getScoreStatus = (val) => {
        if (val === null || val === undefined) return { text: 'Pending', color: '#6c757d' };
        if (val >= 80) return { text: 'Excellent', color: '#28a745' };
        if (val >= 60) return { text: 'Good', color: '#17a2b8' };
        if (val >= 40) return { text: 'Average', color: '#f0ad4e' };
        return { text: 'Needs Improvement', color: '#dc3545' };
      };
      const scoreStatus = getScoreStatus(appraisal.final_score);
      const finalScoreDisplay = appraisal.final_score !== null ? appraisal.final_score + '%' : 'Pending';

      return `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Performance Appraisal Report - ${appraisal.appraised_user?.name || 'Employee'}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: Arial, Helvetica, sans-serif;
              font-size: 11px;
              line-height: 1.4;
              color: #333;
              padding: 30px 40px;
              max-width: 800px;
              margin: 0 auto;
            }

            /* Header */
            .report-header {
              text-align: center;
              margin-bottom: 30px;
              padding-bottom: 20px;
              border-bottom: 2px solid #007bff;
            }
            .report-title {
              font-size: 22px;
              font-weight: bold;
              color: #1a1a2e;
              margin-bottom: 8px;
              letter-spacing: 1px;
            }
            .report-subtitle {
              font-size: 14px;
              color: #007bff;
              margin-bottom: 8px;
            }
            .report-date {
              font-size: 10px;
              color: #888;
            }

            /* Info Grid */
            .info-grid {
              display: table;
              width: 100%;
              margin-bottom: 25px;
              border: 1px solid #ddd;
              border-radius: 6px;
              overflow: hidden;
            }
            .info-row {
              display: table-row;
            }
            .info-cell {
              display: table-cell;
              padding: 12px 15px;
              border-right: 1px solid #eee;
              border-bottom: 1px solid #eee;
              vertical-align: top;
              width: 25%;
            }
            .info-cell:last-child {
              border-right: none;
            }
            .info-row:last-child .info-cell {
              border-bottom: none;
            }
            .info-label {
              font-size: 9px;
              text-transform: uppercase;
              color: #888;
              letter-spacing: 0.5px;
              margin-bottom: 4px;
            }
            .info-value {
              font-size: 13px;
              font-weight: 600;
              color: #1a1a2e;
            }
            .info-value.score-pending { color: #6c757d; }
            .info-value.score-excellent { color: #28a745; }
            .info-value.score-good { color: #17a2b8; }
            .info-value.score-average { color: #f0ad4e; }
            .info-value.score-low { color: #dc3545; }

            /* Section Title */
            .section-title {
              font-size: 13px;
              font-weight: bold;
              color: #1a1a2e;
              margin: 25px 0 12px 0;
              padding-bottom: 6px;
              border-bottom: 1px solid #ddd;
            }

            /* Progress Steps */
            .progress-container {
              display: table;
              width: 100%;
              margin: 15px 0 25px 0;
            }
            .progress-step-wrapper {
              display: table-cell;
              text-align: center;
              vertical-align: middle;
              position: relative;
            }
            .progress-circle {
              display: inline-block;
              width: 26px;
              height: 26px;
              line-height: 26px;
              border-radius: 50%;
              font-size: 11px;
              font-weight: bold;
              text-align: center;
            }
            .progress-circle.completed {
              background-color: #28a745;
              color: white;
            }
            .progress-circle.pending {
              background-color: #e9ecef;
              color: #6c757d;
            }
            .progress-label {
              display: block;
              font-size: 9px;
              color: #666;
              margin-top: 4px;
            }
            .progress-line-wrapper {
              display: table-cell;
              vertical-align: middle;
              width: 40px;
              padding: 0 5px;
            }
            .progress-line {
              height: 3px;
              background-color: #e9ecef;
              width: 100%;
            }
            .progress-line.active {
              background-color: #28a745;
            }

            /* Table */
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
              font-size: 11px;
            }
            th {
              background-color: #343a40;
              color: white;
              padding: 10px 8px;
              text-align: left;
              font-size: 10px;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 0.3px;
            }
            td {
              padding: 10px 8px;
              border-bottom: 1px solid #eee;
              vertical-align: top;
            }
            .center { text-align: center; }
            .category-header {
              background-color: #007bff !important;
              color: white !important;
              font-weight: 600;
              font-size: 11px;
              padding: 8px 12px !important;
            }
            .criteria-row td {
              background-color: #fff;
            }
            .criteria-name {
              font-weight: 600;
              color: #1a1a2e;
              margin-bottom: 2px;
            }
            .criteria-desc {
              font-size: 10px;
              color: #666;
              font-style: italic;
            }
            .score-cell {
              font-weight: 600;
              font-size: 12px;
            }
            .bonus-tag {
              display: inline-block;
              font-size: 8px;
              background: #fff3cd;
              color: #856404;
              padding: 1px 4px;
              border-radius: 3px;
              margin-top: 2px;
            }
            .comments-row td {
              background-color: #f8f9fa;
              padding: 8px 12px;
              border-bottom: 2px solid #dee2e6;
            }
            .comments-cell {
              font-size: 10px;
            }
            .comment-item {
              margin-bottom: 4px;
            }
            .comment-item:last-child {
              margin-bottom: 0;
            }
            .comment-label {
              font-weight: 600;
              color: #495057;
            }

            /* Dispute Box */
            .dispute-box {
              margin: 15px 0;
              padding: 12px 15px;
              border: 2px solid #ffc107;
              background-color: #fff3cd;
              border-radius: 6px;
            }
            .dispute-box.resolved {
              border-color: #28a745;
              background-color: #d4edda;
            }
            .dispute-title {
              font-weight: 600;
              color: #856404;
              margin-bottom: 4px;
            }
            .dispute-box.resolved .dispute-title {
              color: #155724;
            }
            .dispute-reason {
              font-size: 10px;
              color: #856404;
            }
            .dispute-box.resolved .dispute-reason {
              color: #155724;
            }

            /* Summary */
            .summary-box {
              text-align: center;
              padding: 25px;
              margin-top: 30px;
              background-color: #f8f9fa;
              border: 1px solid #ddd;
              border-radius: 8px;
            }
            .summary-label {
              font-size: 10px;
              text-transform: uppercase;
              letter-spacing: 1px;
              color: #666;
              margin-bottom: 8px;
            }
            .summary-score {
              font-size: 42px;
              font-weight: bold;
              color: ${scoreStatus.color};
              line-height: 1;
              margin-bottom: 8px;
            }
            .summary-status {
              font-size: 14px;
              font-weight: 500;
              color: ${scoreStatus.color};
            }

            /* Footer */
            .footer {
              margin-top: 40px;
              padding-top: 15px;
              border-top: 1px solid #ddd;
              text-align: center;
              font-size: 9px;
              color: #999;
            }

            @media print {
              body { padding: 0; }
              @page { margin: 1.5cm; size: A4; }
              .category-header { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              th { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              .progress-circle { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              .progress-line { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          <div class="report-header">
            <div class="report-title">PERFORMANCE APPRAISAL REPORT</div>
            <div class="report-subtitle">${appraisal.name || 'Appraisal'}</div>
            <div class="report-date">Generated on ${new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
          </div>

          <div class="info-grid">
            <div class="info-row">
              <div class="info-cell">
                <div class="info-label">Period</div>
                <div class="info-value">${this.formatPeriod}</div>
              </div>
              <div class="info-cell">
                <div class="info-label">Employee</div>
                <div class="info-value">${appraisal.appraised_user?.name || '-'}</div>
              </div>
              <div class="info-cell">
                <div class="info-label">Manager</div>
                <div class="info-value">${appraisal.manager?.name || '-'}</div>
              </div>
              <div class="info-cell">
                <div class="info-label">Final Score</div>
                <div class="info-value ${appraisal.final_score === null ? 'score-pending' : (appraisal.final_score >= 80 ? 'score-excellent' : (appraisal.final_score >= 60 ? 'score-good' : (appraisal.final_score >= 40 ? 'score-average' : 'score-low')))}">${finalScoreDisplay}</div>
              </div>
            </div>
          </div>

          <div class="section-title">Workflow Progress</div>
          <div class="progress-container">
            <div class="progress-step-wrapper">
              <span class="progress-circle completed">✓</span>
              <span class="progress-label">Assigned</span>
            </div>
            <div class="progress-line-wrapper">
              <div class="progress-line ${appraisal.self_score_submitted ? 'active' : ''}"></div>
            </div>
            <div class="progress-step-wrapper">
              <span class="progress-circle ${appraisal.self_score_submitted ? 'completed' : 'pending'}">${appraisal.self_score_submitted ? '✓' : '2'}</span>
              <span class="progress-label">Self Assessment</span>
            </div>
            <div class="progress-line-wrapper">
              <div class="progress-line ${appraisal.manager_score_submitted ? 'active' : ''}"></div>
            </div>
            <div class="progress-step-wrapper">
              <span class="progress-circle ${appraisal.manager_score_submitted ? 'completed' : 'pending'}">${appraisal.manager_score_submitted ? '✓' : '3'}</span>
              <span class="progress-label">Manager Review</span>
            </div>
            <div class="progress-line-wrapper">
              <div class="progress-line ${appraisal.final_score !== null ? 'active' : ''}"></div>
            </div>
            <div class="progress-step-wrapper">
              <span class="progress-circle ${appraisal.final_score !== null ? 'completed' : 'pending'}">${appraisal.final_score !== null ? '✓' : '4'}</span>
              <span class="progress-label">Completed</span>
            </div>
          </div>

          ${disputeSection}

          <div class="section-title">Evaluation Criteria</div>
          <table>
            <thead>
              <tr>
                <th style="width: 30px;" class="center">#</th>
                <th>Criteria</th>
                <th style="width: 65px;" class="center">Weight</th>
                <th style="width: 50px;" class="center">Self</th>
                <th style="width: 60px;" class="center">Manager</th>
                <th style="width: 60px;" class="center">Reviewer</th>
              </tr>
            </thead>
            <tbody>
              ${criteriaRows}
            </tbody>
          </table>

          <div class="summary-box">
            <div class="summary-label">Overall Performance Score</div>
            <div class="summary-score">${finalScoreDisplay}</div>
            ${appraisal.final_score !== null ? `<div class="summary-status">${scoreStatus.text}</div>` : ''}
          </div>

          <div class="footer">
            This report was generated from the Activity Management System (AMS).<br>
            Confidential - For internal use only.
          </div>
        </body>
        </html>
      `;
    }
  }
};
</script>

<style scoped>
.appraisal-detail-page {
  padding: 16px;
}

/* Header */
.page-header {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.header-title {
  margin-bottom: 20px;
}

.status-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #1a1a2e;
  margin-bottom: 4px;
}

.page-subtitle {
  color: #6c757d;
  margin: 0;
  font-size: 14px;
}

.btn-back {
  border-radius: 8px;
}

/* Status Cards */
.status-cards-wrapper {
  display: flex;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
}

.status-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: #f8f9fa;
  border-radius: 10px;
  min-width: 160px;
}

.status-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.status-icon.period { background: #e3f2fd; color: #1976d2; }
.status-icon.employee { background: #f3e5f5; color: #7b1fa2; }
.status-icon.manager { background: #fff3e0; color: #f57c00; }
.status-icon.reviewer { background: #e0f7fa; color: #00838f; }
.status-icon.score { background: #e8f5e9; color: #388e3c; }
.status-icon.score.pending { background: #f5f5f5; color: #9e9e9e; }
.status-icon.score.excellent { background: #e8f5e9; color: #2e7d32; }
.status-icon.score.good { background: #e3f2fd; color: #1976d2; }
.status-icon.score.average { background: #fff3e0; color: #f57c00; }
.status-icon.score.needs-improvement { background: #ffebee; color: #c62828; }

.status-info {
  display: flex;
  flex-direction: column;
}

.status-label {
  font-size: 12px;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-value {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
}

.score-value.excellent { color: #2e7d32; }
.score-value.good { color: #1976d2; }
.score-value.average { color: #f57c00; }
.score-value.needs-improvement { color: #c62828; }
.score-value.pending { color: #9e9e9e; }

/* Progress Card - Separate Row */
.progress-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.progress-steps {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px;
}

.progress-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.step-circle {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #e9ecef;
  color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.progress-step.completed .step-circle {
  background: #28a745;
  color: #fff;
}

.progress-step.active .step-circle {
  background: #007bff;
  color: #fff;
  box-shadow: 0 0 0 4px rgba(0, 123, 255, 0.2);
}

.step-label {
  font-size: 12px;
  color: #6c757d;
  white-space: nowrap;
}

.progress-step.completed .step-label,
.progress-step.active .step-label {
  color: #1a1a2e;
  font-weight: 500;
}

.progress-line {
  width: 80px;
  height: 3px;
  background: #e9ecef;
  margin: 0 8px;
  margin-bottom: 28px;
  border-radius: 2px;
  transition: background 0.3s ease;
}

.progress-line.active {
  background: #28a745;
}

/* Criteria Section */
.criteria-section {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e9ecef;
}

.section-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.section-title i {
  color: #007bff;
}

.criteria-count {
  font-size: 13px;
  color: #6c757d;
  background: #f8f9fa;
  padding: 4px 12px;
  border-radius: 20px;
}

/* Criteria Groups */
.criteria-list {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.criteria-group {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.category-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: #007bff;
  border-radius: 10px;
  color: #fff;
}

.category-header i {
  font-size: 16px;
  opacity: 0.9;
}

.category-name {
  font-size: 15px;
  font-weight: 600;
  flex: 1;
}

.category-count {
  font-size: 12px;
  background: rgba(255, 255, 255, 0.2);
  padding: 4px 10px;
  border-radius: 12px;
}

/* Criteria Items */

.criteria-item {
  border: 1px solid #e9ecef;
  border-radius: 10px;
  overflow: hidden;
  transition: box-shadow 0.2s ease;
}

.criteria-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.criteria-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.criteria-info {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.criteria-number {
  width: 28px;
  height: 28px;
  background: #007bff;
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  flex-shrink: 0;
}

.criteria-name {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 4px 0;
}

.criteria-description {
  font-size: 13px;
  color: #6c757d;
  margin: 0;
}

.weight-badge {
  background: #e9ecef;
  color: #495057;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
}

.weight-badge.bonus {
  background: #fff3cd;
  color: #856404;
}

/* Attained Weight Badge */
.attained-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.2s ease;
}

.attained-badge i {
  font-size: 10px;
}

.attained-excellent {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.attained-good {
  background: #cce5ff;
  color: #004085;
  border: 1px solid #b8daff;
}

.attained-average {
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffeeba;
}

.attained-low {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.criteria-badges {
  display: flex;
  align-items: center;
  gap: 8px;
}

.criteria-actions {
  display: flex;
  align-items: center;
}

.criteria-actions .btn-icon {
  padding: 4px 8px;
  line-height: 1;
}

.criteria-actions .btn-icon i {
  font-size: 12px;
}

/* Add Criteria Modal */
.existing-criteria-section,
.new-criteria-section {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
}

.section-label {
  font-size: 14px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
}

.criteria-weights-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.criteria-weight-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.criteria-weight-row.is-bonus {
  background: #fff8e1;
  border-color: #ffecb3;
}

.criteria-weight-name {
  flex: 1;
  font-size: 14px;
  color: #212529;
}

.criteria-weight-input {
  display: flex;
  align-items: center;
  gap: 4px;
}

.criteria-weight-input .weight-input {
  width: 70px;
  text-align: right;
}

.weight-suffix {
  color: #6c757d;
  font-size: 14px;
}

.weight-summary {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 12px 16px;
  border: 2px solid #e9ecef;
}

.weight-summary.is-valid {
  border-color: #28a745;
  background: #d4edda;
}

.weight-summary.is-invalid {
  border-color: #dc3545;
  background: #f8d7da;
}

.weight-summary-row {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  padding: 4px 0;
}

.weight-summary-row.total {
  font-weight: 600;
  border-top: 1px solid #dee2e6;
  margin-top: 4px;
  padding-top: 8px;
}

/* Scoring Grid */
.scoring-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1px;
  background: #e9ecef;
}

.scoring-column {
  background: #fff;
  padding: 20px;
}

.scoring-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e9ecef;
  font-weight: 600;
  font-size: 14px;
}

.scoring-column.self .scoring-header {
  color: #1976d2;
}

.scoring-column.manager .scoring-header {
  color: #f57c00;
}

.scoring-column.reviewer .scoring-header {
  color: #00838f;
}

.submitted-badge {
  margin-left: auto;
  font-size: 12px;
  color: #28a745;
  font-weight: 500;
}

.scoring-body .form-group {
  margin-bottom: 16px;
}

.scoring-body .form-group:last-child {
  margin-bottom: 0;
}

.scoring-body label {
  font-size: 12px;
  font-weight: 500;
  color: #6c757d;
  margin-bottom: 8px;
  display: block;
}

/* Score Selector */
.score-selector {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.score-btn {
  width: 40px;
  height: 40px;
  border: 2px solid #e9ecef;
  background: #fff;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  color: #495057;
  cursor: pointer;
  transition: all 0.2s ease;
}

.score-btn:hover:not(:disabled) {
  border-color: #007bff;
  color: #007bff;
}

.score-btn.active {
  background: #007bff;
  border-color: #007bff;
  color: #fff;
}

.score-btn.manager-btn:hover:not(:disabled) {
  border-color: #f57c00;
  color: #f57c00;
}

.score-btn.manager-btn.active {
  background: #f57c00;
  border-color: #f57c00;
  color: #fff;
}

.score-btn.reviewer-btn:hover:not(:disabled) {
  border-color: #00838f;
  color: #00838f;
}

.score-btn.reviewer-btn.active {
  background: #00838f;
  border-color: #00838f;
  color: #fff;
}

.score-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.form-control-modern {
  border-radius: 8px;
  border: 1px solid #e9ecef;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.form-control-modern:focus {
  border-color: #80bdff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

/* Achievement Display */
.achievement-display {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 8px;
}

.target-display {
  display: flex;
  flex-direction: column;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.target-display .actual-value {
  font-size: 18px;
  font-weight: 600;
  color: #495057;
}

.target-display small {
  font-size: 11px;
  color: #6c757d;
}

.achievement-value {
  font-size: 18px;
  font-weight: 600;
}

.achievement-badge {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.target-comparison {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 8px;
  font-size: 14px;
}

.achievement-excellent { color: #2e7d32; background: #e8f5e9; }
.achievement-good { color: #1976d2; background: #e3f2fd; }
.achievement-average { color: #f57c00; background: #fff3e0; }
.achievement-low { color: #c62828; background: #ffebee; }

/* Action Bar */
.action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e9ecef;
}

.action-hint {
  font-size: 14px;
  color: #6c757d;
}

.action-hint i {
  color: #007bff;
  margin-right: 6px;
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-submit {
  border-radius: 8px;
  padding: 12px 24px;
  font-weight: 500;
}

.btn-draft {
  border-radius: 8px;
  padding: 12px 24px;
  font-weight: 500;
  border-style: dashed;
}

/* Dispute Banner */
.dispute-banner {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px 20px;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 12px;
}

.dispute-icon {
  width: 40px;
  height: 40px;
  background: #ffc107;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #856404;
  font-size: 18px;
  flex-shrink: 0;
}

.dispute-content {
  flex: 1;
}

.dispute-content strong {
  color: #856404;
  font-size: 15px;
}

.dispute-content p {
  font-size: 13px;
  color: #856404;
}

/* Resolved Dispute Banner */
.dispute-banner.resolved {
  background: #d4edda;
  border-color: #28a745;
}

.dispute-banner.resolved .dispute-icon {
  background: #28a745;
  color: #fff;
}

.dispute-banner.resolved .dispute-content strong {
  color: #155724;
}

.dispute-banner.resolved .dispute-content p {
  color: #155724;
}

/* Scores Comparison for Reviewer */
.scores-comparison {
  background: #f0f7ff;
  border: 1px solid #b3d7ff;
  border-radius: 8px;
  padding: 12px;
}

.comparison-item {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
}

.comparison-label {
  color: #666;
  font-size: 13px;
}

.comparison-value {
  font-weight: 600;
  color: #333;
}

/* Responsive */
@media (max-width: 992px) {
  .status-cards {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .scoring-grid {
    grid-template-columns: 1fr;
  }
  
  .progress-line {
    width: 40px;
  }
}

@media (max-width: 576px) {
  .status-cards {
    grid-template-columns: 1fr;
  }

  .header-content {
    flex-direction: column;
    gap: 16px;
  }

  .action-bar {
    flex-direction: column;
    gap: 16px;
  }

  .progress-steps {
    flex-wrap: wrap;
  }
}

/* Header Actions */
.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-print {
  border-radius: 8px;
}

/* Print Styles */
@media print {
  /* Hide navigation, buttons, and interactive elements */
  .header-actions,
  .action-bar,
  .btn-back,
  .btn-print,
  .btn-submit,
  .btn-draft,
  .criteria-actions,
  .score-selector button:not(.active),
  .form-control-modern:not([disabled]),
  textarea:not([disabled]),
  input:not([disabled]) {
    display: none !important;
  }

  /* Reset page styles */
  .appraisal-detail-page {
    padding: 0 !important;
    background: white !important;
  }

  /* Remove shadows and borders for cleaner print */
  .page-header,
  .progress-card,
  .criteria-section,
  .criteria-item {
    box-shadow: none !important;
    border: 1px solid #ddd !important;
    margin-bottom: 16px !important;
    page-break-inside: avoid;
  }

  /* Ensure colors print properly */
  .status-icon,
  .step-circle,
  .category-header,
  .criteria-number,
  .weight-badge,
  .attained-badge,
  .score-btn.active {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }

  /* Status cards layout for print */
  .status-cards-wrapper {
    display: flex !important;
    flex-wrap: wrap !important;
    gap: 12px !important;
  }

  .status-card {
    flex: 0 0 auto !important;
    min-width: 140px !important;
    padding: 12px !important;
  }

  /* Progress steps */
  .progress-steps {
    justify-content: flex-start !important;
    gap: 8px !important;
  }

  .progress-line {
    width: 40px !important;
  }

  /* Scoring grid for print - show all columns */
  .scoring-grid {
    display: block !important;
    background: transparent !important;
  }

  .scoring-column {
    border: 1px solid #ddd !important;
    margin-bottom: 12px !important;
    page-break-inside: avoid;
  }

  /* Show disabled inputs as text */
  .form-control-modern[disabled],
  textarea[disabled],
  input[disabled] {
    border: none !important;
    background: transparent !important;
    padding: 0 !important;
    height: auto !important;
    resize: none !important;
  }

  /* Show active score button only */
  .score-btn.active {
    display: inline-flex !important;
    border: 2px solid #333 !important;
  }

  /* Dispute banner */
  .dispute-banner {
    background: #fff3cd !important;
    border: 2px solid #ffc107 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .dispute-banner.resolved {
    background: #d4edda !important;
    border: 2px solid #28a745 !important;
  }

  /* Page breaks */
  .criteria-group {
    page-break-inside: avoid;
  }

  .criteria-item {
    page-break-inside: avoid;
  }

  /* Print header */
  .page-title {
    font-size: 20px !important;
  }

  .section-header {
    border-bottom: 2px solid #333 !important;
  }

  /* Ensure all text is visible */
  * {
    overflow: visible !important;
  }

  /* Hide scrollbars */
  ::-webkit-scrollbar {
    display: none !important;
  }
}
</style>
